/**
 * Единый источник продуктовых различий между сборками kiosk и tablets.
 * Проект собирается в два продукта: киоск и планшет. Вместо дублирования
 * файлов с суффиксами .kiosk.* / .tablets.* здесь централизованно описаны
 * константы и флаги фич, по которым сборки отличаются.
 */

export type ProductKind = 'kiosk' | 'tablets'

export function detectProductKind(): ProductKind {
  const value = (import.meta.env.VITE_PRODUCT || '').toLowerCase()
  return value === 'kiosk' ? 'kiosk' : 'tablets'
}

export const productKind: ProductKind = detectProductKind()

export const isKiosk = productKind === 'kiosk'
export const isTablets = productKind === 'tablets'

export interface ProductProfile {
  kind: ProductKind
  appVersion: string
  authSource: string
  adBannerName: string
  /** source=cart в /recommended/to-product */
  recommendedSourceCart: boolean
  /** Бейдж сборки поверх приложения (App.vue) */
  buildIcon: boolean
  /** Баннер начисления бонусов (AuthModal) */
  authBanner: boolean
  /** FastSetsModal активен (ProductsView, ProductModal) */
  fastSetsModal: boolean
  /** Sticky-категории с тенью при скролле (Categories) */
  stickyCategories: boolean
  /** Номерные бейджи незаполненных групп (ComboSetSecond) */
  comboStepBadges: boolean
  /** +additional_price у групп комбо (ComboFinalInfo) */
  comboAdditionalPrice: boolean
  /** Механика comboQuestionCounter при клике на комбо-товар (ProductCard, ComboSetFirst) */
  comboQuestionCounter: boolean
  /** Поиск позиции корзины по модам/кондиментам (ProductCard) */
  productCardMatchMods: boolean
  /** Печать чека на localhost:5000 (PaymentCard/Sbp) */
  printReceipt: boolean
  /** Блок i18n settings.* и переключатель языка (SettingView) */
  settingsI18n: boolean
  /** Слайдшоу с видео в AdvertisingView */
  videoSlideshow: boolean
  /** Скрыть скроллбары глобально */
  hideScrollbars: boolean
  /** Компактная планшетная раскладка (мелкие элементы, 3-4 колонки) */
  compactLayout: boolean
  /** Квинтити-степпер в футере ProductModal (редактирование кол-ва) */
  footerQuantityStepper: boolean
  /** Не показывать recommended в ProductModal при withMods */
  hideRecsWithMods: boolean
}

export const productProfile: ProductProfile = isKiosk
  ? {
      kind: 'kiosk',
      appVersion: 'Киоск',
      authSource: 'ksk',
      adBannerName: 'Киоск',
      recommendedSourceCart: true,
      buildIcon: false,
      authBanner: false,
      fastSetsModal: false,
      stickyCategories: false,
      comboStepBadges: false,
      comboAdditionalPrice: false,
      comboQuestionCounter: true,
      productCardMatchMods: true,
      printReceipt: true,
      settingsI18n: true,
      videoSlideshow: true,
      hideScrollbars: false,
      compactLayout: false,
      footerQuantityStepper: true,
      hideRecsWithMods: false,
    }
  : {
      kind: 'tablets',
      appVersion: 'Планшет',
      authSource: 'tablet',
      adBannerName: 'Айпад',
      recommendedSourceCart: false,
      buildIcon: true,
      authBanner: true,
      fastSetsModal: true,
      stickyCategories: true,
      comboStepBadges: true,
      comboAdditionalPrice: true,
      comboQuestionCounter: false,
      productCardMatchMods: false,
      printReceipt: false,
      settingsI18n: false,
      videoSlideshow: false,
      hideScrollbars: true,
      compactLayout: true,
      footerQuantityStepper: false,
      hideRecsWithMods: true,
    }